import React from 'react';
import {
  BiBriefcase,
  BiBuildings,
  BiFile,
  BiLinkAlt,
  BiCloudUpload,
  BiMap,
  BiRupee,
  BiShield,
  BiTime
} from 'react-icons/bi';
import { Navigate, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import WorkspacePageHeader from '../../../components/workspace/WorkspacePageHeader';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import { canManageMasters } from '../../../utils/permissions';
import './MastersPage.scss';

const MASTERS = [
  {
    id: 'customers',
    title: 'Clients / Authorities',
    description: 'Authorities and private clients used when a tender is captured.',
    icon: BiBuildings,
    to: '/apps/tender/masters/customers'
  },
  {
    id: 'services',
    title: 'Services',
    description: 'DPR, PMC, authority engineer, and the other services the firm offers.',
    icon: BiBriefcase,
    to: '/apps/tender/masters/services'
  },
  {
    id: 'sectors',
    title: 'Sectors',
    description: 'Roads, bridges, metro, water, and the other sectors you pursue.',
    icon: BiMap,
    to: '/apps/tender/masters/sectors'
  },
  {
    id: 'tender-types',
    title: 'Tender types',
    description: 'Government RFP, public procurement, and the other forms a bid can take.',
    icon: BiFile,
    to: '/apps/tender/masters/tender-types'
  },
  {
    id: 'sources',
    title: 'Sources',
    description: 'Portals and channels where opportunities are found.',
    icon: BiLinkAlt,
    to: '/apps/tender/masters/sources'
  },
  {
    id: 'issuers',
    title: 'Banks and issuers',
    description: 'Banks and issuers used when a BG or EMD is recorded.',
    icon: BiShield,
    to: '/apps/tender/masters/issuers'
  },
  {
    id: 'partners',
    title: 'Partners',
    description: 'JV and consortium partners used on a tender. Due diligence stays on the tender.',
    icon: BiBuildings,
    to: '/apps/tender/masters/partners'
  },
  {
    id: 'decision-profiles',
    title: 'Go / No-Go profiles',
    description: 'Weighted criteria, gates, and thresholds. Sample weights are not a confirmed policy.',
    icon: BiFile,
    to: '/apps/tender/masters/decision-profiles'
  },
  {
    id: 'timesheet-activities',
    title: 'Timesheet activities',
    description: 'Activities used when time is booked to a tender or an assignment.',
    icon: BiTime,
    to: '/apps/tender/masters/timesheet-activities'
  },
  {
    id: 'document-types',
    title: 'Document types',
    description: 'Names for uploaded files. A type does not grant access and does not make a file eligible for AI.',
    icon: BiFile,
    to: '/apps/tender/masters/lists/document-types'
  },
  {
    id: 'designations',
    title: 'Designations',
    description: 'Job titles for personnel and costing rates. A designation is not an application access role.',
    icon: BiBriefcase,
    to: '/apps/tender/masters/lists/personnel-designations'
  },
  {
    id: 'cost-heads',
    title: 'Cost heads',
    description: 'Estimate lines. The basis is one the costing engine already calculates. Starting heads are editable.',
    icon: BiRupee,
    to: '/apps/tender/masters/lists/cost-heads'
  },
  {
    id: 'instrument-types',
    title: 'Security / instrument types',
    description: 'EMD, bid security, and performance BG. Lifecycle states stay controlled.',
    icon: BiShield,
    to: '/apps/tender/masters/lists/instrument-types'
  },
  {
    id: 'diligence',
    title: 'Due-diligence checklists',
    description: 'Versioned partner checks. A review keeps the template version it started with.',
    icon: BiFile,
    to: '/apps/tender/masters/diligence'
  },
  {
    id: 'outcome-reasons',
    title: 'Outcome reasons',
    description: 'Reasons for Lost, Withdrawn, Cancelled, and No-Go. The outcome code itself stays controlled.',
    icon: BiFile,
    to: '/apps/tender/masters/lists/outcome-reasons'
  },
  {
    id: 'rates',
    title: 'Costing Rates',
    description: 'Internal effort-cost rates and charge-out rates by designation. An unclassified rate is not used in a calculation.',
    icon: BiRupee,
    to: '/apps/tender/masters/rates'
  },
  {
    id: 'upload',
    title: 'Upload Center',
    description: 'Download an Excel template for each master and upload filled rows into the list.',
    icon: BiCloudUpload,
    to: '/apps/tender/masters/upload'
  }
];

const MastersPage = () => {
  const user = useSelector((state) => state.auth.user);
  const navigate = useNavigate();
  useDocumentTitle('Masters');

  if (!canManageMasters(user?.permissions, user?.roles)) {
    return <Navigate to="/apps/tender" replace />;
  }

  return (
    <div className="mst-catalog">
      <WorkspacePageHeader
        title="Masters"
        help="masters"
        subtitle="Clients, services, document types, designations, cost heads, instrument types, checklists, outcome reasons, and costing rates. Locations, users, stages, and approval rules stay in their existing screens."
      />
      <div className="mst-catalog__grid">
        {MASTERS.map((master) => {
          const Icon = master.icon;
          const Tag = master.to ? 'button' : 'article';
          return (
            <Tag
              key={master.id}
              className="mst-catalog__card"
              type={master.to ? 'button' : undefined}
              onClick={master.to ? () => navigate(master.to) : undefined}
            >
              <div className="mst-catalog__card-head">
                <span className="mst-catalog__icon" aria-hidden>
                  <Icon />
                </span>
                <h2>{master.title}</h2>
              </div>
              <p>{master.description}</p>
            </Tag>
          );
        })}
      </div>
      <p className="text-muted small mt-3 mb-1">Already kept elsewhere</p>
      <div className="d-flex flex-wrap gap-3">
        <button type="button" className="btn btn-link p-0" onClick={() => navigate('/apps/tender/settings')}>Company setup, tender stages, priority, urgency, approval rules, reminders, and timesheet rules</button>
        <button type="button" className="btn btn-link p-0" onClick={() => navigate('/apps/tender/settings/users')}>Users and access roles</button>
        <button type="button" className="btn btn-link p-0" onClick={() => navigate('/apps/tender/personnel')}>Personnel records</button>
      </div>
    </div>
  );
};

export default MastersPage;
